'use client';

import { CalendarWeekAnalysis } from './day/CalendarWeekAnalysis';
import Loading from '@/app/components/Loading';
import SportIcon from '@/app/activities/SportIcon';
import type { Week } from './InfiniteCalendar';
import WeeklyFinishChart, { DisplayMetric } from './WeeklyFinishChart';
import { formatDistance } from '@trainme/api/client';
import { secondsToHm } from '@/utils/timeUtils';
import { startOfWeek } from 'date-fns';
import { trpc } from '@/app/api/trpc/client';
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  KeyboardEvent,
} from 'react';
import type {
  ActivityWithRelations,
  WorkoutWithRelations,
} from '@/utils/types';
import { buildWeeklySummaryFromActivitiesAndWorkouts } from './utils/weeklySummary';
import { useUnitSystem } from '@/app/hooks/useUnitSystem';

export type WeekSummaryVariant = 'calendar' | 'dashboard';

interface CalendarWeekSummaryProps {
  week: Week;
  activities?: ActivityWithRelations[];
  workouts?: WorkoutWithRelations[];
  variant?: WeekSummaryVariant;
}

export const CalendarWeekSummary = ({
  week,
  activities,
  workouts,
  variant = 'calendar',
}: CalendarWeekSummaryProps) => {
  const currentWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  const [selectedSportId, setSelectedSportId] = useState<string | null>(null);
  const [displayMetric, setDisplayMetric] = useState<DisplayMetric>('time');
  const isFutureWeek = week.start.getTime() > currentWeekStart.getTime();
  const effectiveSelectedSportId = isFutureWeek ? null : selectedSportId;

  const handleSelectSport = useCallback(
    (sportType: string) => {
      if (!isFutureWeek) {
        setSelectedSportId(sportType);
      }
    },
    [isFutureWeek, setSelectedSportId]
  );

  const handleSelectMetric = useCallback(
    (metric: DisplayMetric) => setDisplayMetric(metric),
    [setDisplayMetric]
  );

  return (
    <div
      className={`calendar-week-summary overflow-hidden ${
        variant === 'calendar'
          ? 'card border border-gray-300 dark:border-gray-600'
          : 'border-0 bg-transparent shadow-none'
      }`}
    >
      {variant === 'calendar' && (
        <div className='card-header card-header-gradient flex items-start justify-between gap-2 border-gray-300 dark:border-gray-600'>
          <div className='flex flex-col gap-1'>
            <span>Week Summary</span>
          </div>
          <div className='flex items-center gap-2 min-w-0'>
            <CalendarWeekAnalysis
              week={week}
              activitiesCount={activities?.length ?? 0}
              workoutsCount={workouts?.length ?? 0}
            />
          </div>
        </div>
      )}
      <div
        className={`card-body p-2 ${variant === 'dashboard' ? 'grid gap-4 sm:grid-cols-12 sm:items-center' : 'flex flex-col gap-4'}`}
      >
        {!isFutureWeek && (
          <div
            className={variant === 'dashboard' ? 'sm:col-span-4' : undefined}
          >
            <WeeklyFinishChart
              week={week}
              selectedSportId={effectiveSelectedSportId}
              displayMetric={displayMetric}
              onSelectSport={handleSelectSport}
              activities={activities}
              workouts={workouts}
              variant={variant}
            />
          </div>
        )}
        <div className={variant === 'dashboard' ? 'sm:col-span-8' : undefined}>
          <CalendarWeekActivitiesSummary
            week={week}
            selectedSportId={effectiveSelectedSportId}
            activities={activities}
            workouts={workouts}
            variant={variant}
            displayMetric={displayMetric}
            onSelectSport={handleSelectSport}
            onSelectMetric={handleSelectMetric}
            isFutureWeek={isFutureWeek}
          />
        </div>
      </div>
    </div>
  );
};

type CalendarWeekActivitiesSummaryProps = {
  week: Week;
  selectedSportId?: string | null;
  onSelectSport?: (sportType: string) => void;
  displayMetric: DisplayMetric;
  onSelectMetric?: (metric: DisplayMetric) => void;
  isFutureWeek: boolean;
  activities?: ActivityWithRelations[];
  workouts?: WorkoutWithRelations[];
  variant: WeekSummaryVariant;
};

const CalendarWeekActivitiesSummaryComponent = ({
  week,
  selectedSportId,
  onSelectSport,
  displayMetric,
  onSelectMetric,
  isFutureWeek,
  activities,
  workouts,
  variant,
}: CalendarWeekActivitiesSummaryProps) => {
  const summaryQuery = trpc.activities.getWeeklySummary.useQuery({
    aday: week.start,
    filter: {
      date: {
        gte: week.start,
        lt: week.end,
      },
    },
  });

  const weeklySummary = useMemo(
    () => summaryQuery.data ?? [],
    [summaryQuery.data]
  );
  const isLoading = summaryQuery.isLoading;

  const fallbackSummary = useMemo(
    () => buildWeeklySummaryFromActivitiesAndWorkouts(activities, workouts),
    [activities, workouts]
  );

  const summaryRows =
    weeklySummary.length > 0 ? weeklySummary : fallbackSummary;

  const unitSystem = useUnitSystem();

  useEffect(() => {
    if (isFutureWeek || isLoading || summaryRows.length === 0) return;
    if (!selectedSportId) {
      const defaultSport = summaryRows[0]?.sportType;
      if (defaultSport) {
        onSelectSport?.(defaultSport);
      }
    }
  }, [summaryRows, selectedSportId, onSelectSport, isLoading, isFutureWeek]);

  if (isLoading) {
    return (
      <Loading
        size={16}
        className='h-14 text-emerald-400 dark:text-emerald-800'
      />
    );
  }
  const formatPlannedDistance = (plannedKilometers?: number) => {
    const plannedMeters = (plannedKilometers ?? 0) * 1000;
    return formatDistance(plannedMeters, unitSystem);
  };

  const handleKeyDown =
    (sportType: string) => (event: KeyboardEvent<HTMLTableRowElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelectSport?.(sportType);
      }
    };

  const handleMetricKeyDown =
    (metric: DisplayMetric) => (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelectMetric?.(metric);
      }
    };

  const hasSummaryRows = summaryRows.length > 0;
  const isTimeMetric = displayMetric === 'time';
  const totals = summaryRows.reduce(
    (acc, entry) => {
      acc.doneTime += entry.doneTime ?? 0;
      acc.plannedTime += (entry.plannedTime ?? 0) * 60;
      acc.doneDistance += entry.doneDistance ?? 0;
      acc.plannedDistance += entry.plannedDistance ?? 0;
      return acc;
    },
    {
      doneTime: 0,
      plannedTime: 0,
      doneDistance: 0,
      plannedDistance: 0,
    }
  );
  const totalDoneTimeLabel = secondsToHm(totals.doneTime) || '0m';
  const totalPlannedTimeLabel = secondsToHm(totals.plannedTime) || '0m';
  const totalDoneDistanceLabel = formatDistance(
    totals.doneDistance,
    unitSystem
  );
  const totalPlannedDistanceLabel = formatPlannedDistance(
    totals.plannedDistance
  );
  const activeMetricColumnClass = 'bg-emerald-500/10';
  const metricColumnBaseClass = 'px-1 py-0.5';

  return (
    <div
      className={`max-w-full overflow-x-auto ${variant === 'dashboard' ? 'text-base' : 'text-sm'}`}
    >
      <div className='w-full'>
        {hasSummaryRows && (
          <div className='flex items-center justify-between text-right uppercase text-gray-600 dark:text-gray-400'>
            <div className='text-left text-[0.9rem] font-semibold tracking-wide'>
              Sports
            </div>
            <div className='w-2/3 inline-flex items-center rounded-full border border-emerald-500/60 bg-transparent overflow-hidden'>
              <button
                type='button'
                onClick={() => onSelectMetric?.('time')}
                onKeyDown={handleMetricKeyDown('time')}
                className={`flex-1 p-1 text-xs font-semibold uppercase leading-tight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-1 transition ${
                  displayMetric === 'time'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-transparent  hover:text-white'
                } rounded-l-full`}
              >
                Time
              </button>
              <button
                type='button'
                onClick={() => onSelectMetric?.('distance')}
                onKeyDown={handleMetricKeyDown('distance')}
                className={`flex-1 p-1 text-xs font-semibold uppercase leading-tight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-1 transition ${
                  displayMetric === 'distance'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-transparent hover:text-white'
                } rounded-r-full`}
              >
                Dist
              </button>
            </div>
          </div>
        )}
        <div className='mt-2 relative'>
          {hasSummaryRows ? (
            <>
              <div
                className={`pointer-events-none absolute inset-y-0 ${
                  isTimeMetric ? 'left-1/3 right-1/3' : 'left-2/3 right-0'
                } rounded-md ${activeMetricColumnClass}`}
              />
              {summaryRows.map((entry) => {
                const sportType = entry.sportType || '';
                const doneTimeLabel = secondsToHm(entry.doneTime ?? 0) || '0m';
                const plannedTimeLabel =
                  secondsToHm((entry.plannedTime ?? 0) * 60) || '0m';
                const doneDistanceLabel = formatDistance(
                  entry.doneDistance ?? 0,
                  unitSystem
                );
                const plannedDistanceLabel = formatPlannedDistance(
                  entry.plannedDistance
                );
                const isSelected = selectedSportId === sportType;
                const showDoneValues = !isFutureWeek;
                return (
                  <div
                    key={sportType}
                    role='button'
                    onClick={() => onSelectSport?.(sportType)}
                    onKeyDown={handleKeyDown(sportType)}
                    className={`relative z-10 cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ${
                      isSelected
                        ? 'bg-gray-200/80 text-gray-800 dark:bg-gray-800/60 dark:text-gray-200'
                        : 'hover:bg-white/10 dark:hover:bg-white/5'
                    }`}
                  >
                    <div className='text-left grid grid-cols-3 items-center gap-2 py-1 px-2'>
                      <div className='flex items-center gap-2'>
                        <SportIcon type={sportType} size={16} />
                        <div
                          className={`grid text-[0.85em] uppercase text-gray-900 dark:text-white ${
                            showDoneValues
                              ? 'grid-rows-2 gap-0.5'
                              : 'grid-rows-1'
                          }`}
                        >
                          {showDoneValues && <span>Done</span>}
                          <span className='text-gray-900 dark:text-gray-400'>
                            Plan
                          </span>
                        </div>
                      </div>
                      <div
                        className={`${metricColumnBaseClass} text-right space-y-0.5`}
                      >
                        {showDoneValues && (
                          <div className='whitespace-nowrap'>
                            {doneTimeLabel}
                          </div>
                        )}
                        <div className='text-gray-900 dark:text-gray-400 whitespace-nowrap'>
                          {plannedTimeLabel}
                        </div>
                      </div>
                      <div
                        className={`${metricColumnBaseClass} text-right space-y-0.5`}
                      >
                        {showDoneValues && (
                          <div className='whitespace-nowrap'>
                            {doneDistanceLabel}
                          </div>
                        )}
                        <div className='text-gray-900 dark:text-gray-400 whitespace-nowrap'>
                          {plannedDistanceLabel}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className='relative z-10 mt-1 border-t border-gray-300/40 dark:border-gray-700/40 bg-white/5 dark:bg-white/5'>
                <div className='grid grid-cols-3 items-center gap-2 py-2 px-2 font-semibold'>
                  <div className='flex items-center gap-2'>
                    <div className='text-[0.85em] uppercase text-gray-900 dark:text-white'>
                      Total
                    </div>
                  </div>
                  <div className='text-right space-y-0.5'>
                    {!isFutureWeek && (
                      <div className='whitespace-nowrap'>
                        {totalDoneTimeLabel}
                      </div>
                    )}
                    <div className='text-gray-900 dark:text-gray-400 whitespace-nowrap'>
                      {totalPlannedTimeLabel}
                    </div>
                  </div>
                  <div className='text-right space-y-0.5'>
                    {!isFutureWeek && (
                      <div className='whitespace-nowrap'>
                        {totalDoneDistanceLabel}
                      </div>
                    )}
                    <div className='text-gray-900 dark:text-gray-400 whitespace-nowrap'>
                      {totalPlannedDistanceLabel}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className='text-center text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400'>
              No activities or planned workouts
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const areItemListsEqual = <T extends { id?: string | number }>(
  prev?: T[],
  next?: T[]
) => {
  if (prev === next) return true;
  if (!prev || !next) return prev === next;
  if (prev.length !== next.length) return false;
  for (let i = 0; i < prev.length; i += 1) {
    if (prev[i]?.id !== next[i]?.id) {
      return false;
    }
  }
  return true;
};

const areSummaryPropsEqual = (
  prev: CalendarWeekActivitiesSummaryProps,
  next: CalendarWeekActivitiesSummaryProps
) => {
  if (prev.week.start.getTime() !== next.week.start.getTime()) return false;
  if (prev.week.end.getTime() !== next.week.end.getTime()) return false;
  if (prev.selectedSportId !== next.selectedSportId) return false;
  if (prev.displayMetric !== next.displayMetric) return false;
  if (prev.isFutureWeek !== next.isFutureWeek) return false;
  if (prev.variant !== next.variant) return false;
  if (prev.onSelectSport !== next.onSelectSport) return false;
  if (prev.onSelectMetric !== next.onSelectMetric) return false;
  if (!areItemListsEqual(prev.activities, next.activities)) return false;
  if (!areItemListsEqual(prev.workouts, next.workouts)) return false;
  return true;
};

const CalendarWeekActivitiesSummary = memo(
  CalendarWeekActivitiesSummaryComponent,
  areSummaryPropsEqual
);
